<template>
  <div class="employee-payroll-table">
    <DataTable
      ref="datatableRef"
      :config="datatableConfig"
      :data="employeeFranchiseRows"
      :refreshButton="refreshButtonConfig"
      :autoLoad="true"
      @row-click="handleRowClick"
      @button-click="handleButtonClick"
      @refresh="loadData"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEmployeePayroll } from '@/composables/useEmployeePayroll';
import { eventBus, type EventCallback } from '@/eventBus';
import { log } from '@/utils/DebugLogger';

type Id = number | string;

interface CellParams {
  value: unknown;
  data?: Record<string, unknown>;
}

interface EmployeeRow {
  user_id: Id;
  user_name: string;
  franchise_id: Id;
  franchise_name: string;
  latest_period_end?: string | null;
  days_since_last_payroll?: number | null;
  latest_period_status?: number | null;
  has_active_rules?: boolean;
  [key: string]: unknown;
}

interface ButtonActionData {
  action?: string;
  value?: unknown;
  data?: EmployeeRow;
}

const emit = defineEmits<{
  view: [row: EmployeeRow];
  'create-period': [row: EmployeeRow];
  'view-history': [payload: { userId: Id; userName: string; franchiseId: Id; franchiseName: string }];
}>();

const { t } = useI18n();
const {
  listEmployeesPayrollSummary,
  employeeFranchiseRows,
  formatPeriodDate,
  getStatusLabel,
  getStatusColor
} = useEmployeePayroll();

const datatableRef = ref<Record<string, unknown> | null>(null);
const employeesSummary = ref<EmployeeRow[]>([]);

// DataTable Configuration
const datatableConfig = computed(() => {
  return {
    tableId: 'employeePayrollTable',
    pageLength: 25,
    pagination: true,
    columns: tableColumns.value,
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const tableColumns = computed(() => {
  return [
    {
      data: 'user_name',
      title: t('Colaborador'),
      renderType: 'text'
    },
    {
      data: 'franchise_name',
      title: t('Franquia'),
      renderType: 'text'
    },
    {
      data: 'latest_period_end',
      title: t('Último Período'),
      cellRenderer: (params: CellParams): string => {
        return formatPeriodDate(params.value);
      }
    },
    {
      data: 'days_since_last_payroll',
      title: t('Dias Desde'),
      cellRenderer: (params: CellParams): string => {
        if (params.value === null || params.value === undefined) {
          return '<span class="text-gray-500">Nunca</span>';
        }
        const days = params.value as number;
        if (days > 30) {
          return `<span class="text-red-600 font-semibold">${days} dias</span>`;
        } else if (days > 15) {
          return `<span class="text-yellow-600 font-semibold">${days} dias</span>`;
        } else {
          return `<span class="text-green-600">${days} dias</span>`;
        }
      }
    },
    {
      data: 'latest_period_status',
      title: t('Status'),
      cellRenderer: (params: CellParams): string => {
        const status = params.value;
        if (status === null || status === undefined) {
          return '<span class="text-gray-500">Sem período</span>';
        }
        const label = getStatusLabel(status as number);
        const colorClass = getStatusColor(status as number);
        return `<span class="px-2 py-1 rounded-full text-xs font-medium ${colorClass}">${label}</span>`;
      }
    },
    {
      data: 'has_active_rules',
      title: t('Regras Ativas'),
      cellRenderer: (params: CellParams): string => {
        return params.value
          ? '<span class="pi pi-check text-green-600"></span>'
          : '<span class="pi pi-times text-red-600"></span>';
      }
    },
    {
      data: 'user_id',
      title: t('Ações'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Criar Período'),
      buttonIcon: 'pi pi-plus',
      buttonClass: 'p-button-sm p-button-outlined',
      buttonAction: 'create-period'
    },
    {
      data: 'user_id',
      title: '',
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Histórico'),
      buttonIcon: 'pi pi-list',
      buttonClass: 'p-button-sm p-button-outlined p-button-secondary',
      buttonAction: 'view-history'
    }
  ];
});

/**
 * Load employee payroll summary data
 */
const loadData: EventCallback = async (): Promise<void> => {
  try {
    log('EmployeePayrollTable', 'loadData', 'Loading employees payroll summary');
    const data = await listEmployeesPayrollSummary();
    employeesSummary.value = data;
    log('EmployeePayrollTable', 'loadData', 'Loaded summary', data.length, 'employees');
  } catch (err) {
    console.error('[EmployeePayrollTable] Failed to load data:', err);
  }
};

/**
 * Handle row click - disabled, row clicks no longer trigger actions
 * Only buttons should trigger actions
 */
const handleRowClick = (row: unknown): void => {
  log('EmployeePayrollTable', 'handleRowClick', 'Row clicked (no action)', row);
  // Row clicks do nothing - users must click action buttons
};

/**
 * Handle button click
 */
const handleButtonClick = (action: string | ButtonActionData, row: EmployeeRow): void => {
  log('EmployeePayrollTable', 'handleButtonClick', 'Action:', action, 'Row:', row);

  // DataTable passes action as object: { value, data, action }
  const actionType = typeof action === 'object' ? action.action : action;

  switch (actionType) {
    case 'create-period':
      confirmCreatePeriod(row);
      break;
    case 'view-history':
      emit('view-history', {
        userId: row.user_id,
        userName: row.user_name,
        franchiseId: row.franchise_id,
        franchiseName: row.franchise_name
      });
      break;
    default:
      console.warn('[EmployeePayrollTable] Unknown action:', actionType, 'Full action object:', action);
  }
};

/**
 * Confirm and open payroll wizard modal
 */
const confirmCreatePeriod = (row: EmployeeRow): void => {
  log('EmployeePayrollTable', 'confirmCreatePeriod', 'Opening payroll wizard for', row.user_name);

  // Emit event to open PayrollModal (wizard) for creating new period
  eventBus.emit('modal.payroll.open', {
    // No period object = create new mode
    prefill: {
      userId: row.user_id,
      userName: row.user_name,
      franchiseId: row.franchise_id,
      franchiseName: row.franchise_name
    }
  });
};

/**
 * Handle refresh event
 */
onMounted(() => {
  log('EmployeePayrollTable', 'mounted', 'Component mounting');

  // Listen for period creation events to refresh table
  eventBus.on('employeePayroll.periodCreated', loadData);

  // Load initial data
  loadData();
});

onUnmounted(() => {
  log('EmployeePayrollTable', 'unmounted', 'Component unmounting');
  eventBus.off('employeePayroll.periodCreated', loadData);
});
</script>

<style scoped>
.employee-payroll-table {
  width: 100%;
}

.create-period-confirm {
  line-height: 1.5;
}
</style>
